import { Dispatch, ReactNode, SetStateAction, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ActionBar } from '@/lib/components/ActionBar';
import { ItemsListProps } from '@/lib/components/DropDown/DropDown';
import { openModal } from '@/lib/components/Modal/Modal';
import {
  DeleteIconSolid,
  DropdownCellPhoneIcon,
  FlowsIcon,
  GroupAddIconSolid,
  SuspendContactIcon,
  TagAddIconSolid,
} from '@/lib/icon';
import { DropdownMailIcon } from '@/lib/icon/DropdownMailIcon';
import { DropdownPush } from '@/lib/icon/DropdownPushIcon';
import { DropdownSmsIcon } from '@/lib/icon/DropdownSmsIcon';
import { Button, IconSvg, Modal, Text } from '@/lib/v2/components';
import { ExportUserIcon } from '@/lib/v2/icons/outline';
import { ReactivateIcon } from '@/lib/v2/icons/solid';

import { useFeatureFlag } from '@/src/application/hooks/util/useFeatureFlag';
import { ContactsDropDown } from '@/src/ContactsModule/components/ContactsDropDown';
import {
  ContactsToFlowModal,
  DeleteModal,
  SuspendContactModal,
} from '@/src/ContactsModule/components/ContactsModals';
import { quantifier } from '@/src/ContactsModule/utils/quantifier';

export const dropdownOptions = {
  sendingType: [
    {
      title: 'Send Email',
      icon: <DropdownMailIcon />,
      onAction: () => {},
    },
    {
      title: 'Send Sms',
      icon: <DropdownSmsIcon />,
      onAction: () => {},
    },
    {
      title: 'Push Notifications',
      icon: <DropdownPush />,
      onAction: () => {},
    },
  ],
};

const flowsListsAux = [
  {
    id: 1,
    title: 'flows 1',
  },
  {
    id: 2,
    title: 'flows 2',
  },
  {
    id: 3,
    title: 'flows 3',
  },
];

interface IContactsActionBarProps {
  contactSelection: number;
  flowsLists?: ItemsListProps[];
  setFlowId: Dispatch<SetStateAction<number>>;
  setShowGroupsModal: Dispatch<SetStateAction<boolean>>;
  setShowTagsModal: Dispatch<SetStateAction<boolean>>;
  setConfirmDelete: Dispatch<SetStateAction<boolean>>;
  setConfirmSuspend: Dispatch<SetStateAction<boolean>>;
  setConfirmToFlow: Dispatch<SetStateAction<boolean>>;
  onReactivateDiscardedUsers: () => void;
  isValidReactivateDiscardedUsers?: boolean;
  discardedView?: boolean;
}

export const ContactsActionBar = ({
  flowsLists,
  contactSelection,
  setShowGroupsModal,
  setShowTagsModal,
  setConfirmDelete,
  setConfirmSuspend,
  setConfirmToFlow,
  setFlowId,
  isValidReactivateDiscardedUsers,
  onReactivateDiscardedUsers,
  discardedView,
}: IContactsActionBarProps) => {
  const { t } = useTranslation();
  const actionBarDropdown = useFeatureFlag('actionBarDropdown');

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [childrenModal, setChildrenModal] = useState<ReactNode>(null);

  const handleReactivateDiscardedUsers = useCallback(() => {
    setChildrenModal(
      <div className="relative flex flex-col gap-4">
        <Text fontWeight="bold">{t('REACTIVATE DISCARDED USER.title')}</Text>
        <Text>{t('REACTIVATE DISCARDED USER.description')}</Text>
        <Button
          destructive
          id="reactivate-button"
          onClick={() => {
            onReactivateDiscardedUsers();
            setModalIsOpen(false);
          }}
        >
          {t('REACTIVATE DISCARDED USER.reactivate')}
        </Button>
      </div>
    );
    setModalIsOpen(true);
  }, [onReactivateDiscardedUsers]);

  const iconsActions = [
    {
      icon: <IconSvg height="28px" svgComponent={<TagAddIconSolid />} width="28px" />,
      handleOnClick: () => setShowTagsModal(true),
      tooltip: t('CONTACTS_ACTIONS_DROPDOWN.AddToTag'),
      flagOn: useFeatureFlag('actionBarTagAddIcon'),
      disabled: false,
      disabledTooltip: '',
      id: 'action-add-tag',
    },
    {
      icon: <IconSvg height="28px" svgComponent={<GroupAddIconSolid />} width="28px" />,
      handleOnClick: () => setShowGroupsModal(true),
      tooltip: t('CONTACTS_ACTIONS_DROPDOWN.AddToGroup'),
      flagOn: useFeatureFlag('actionBarGroupAddIcon'),
      disabled: false,
      disabledTooltip: '',
      id: 'action-add-group',
    },
    {
      icon: <FlowsIcon />,
      handleOnClick: () =>
        openModal({
          element: (
            <ContactsToFlowModal
              itemList={flowsLists && flowsLists.length > 0 ? flowsLists : flowsListsAux}
              setConfirmToFlow={setConfirmToFlow}
              setFlowId={setFlowId}
            />
          ),
        }),
      tooltip: t('CONTACT_FLOW_MODAL.toolTip'),
      flagOn: useFeatureFlag('actionBarFlowsIcon'),
      disabled: false,
      disabledTooltip: '',
      id: 'action-connect-flow',
    },
    {
      icon: <ExportUserIcon />,
      handleOnClick: () => alert('Export'),
      tooltip: 'Export CSV',
      flagOn: useFeatureFlag('actionBarExportUserIcon'),
      disabled: false,
      disabledTooltip: '',
      id: 'action-export-csv',
    },
    {
      icon: <SuspendContactIcon />,
      handleOnClick: () => {
        setChildrenModal(
          <SuspendContactModal
            onClick={() => {
              setConfirmSuspend(true);
              setModalIsOpen(false);
            }}
          />
        );
        setModalIsOpen(true);
      },
      tooltip: t('CONTACT_SUSPEND_MODAL.title'),
      flagOn: !discardedView,
      disabled: false,
      disabledTooltip: '',
      id: 'action-suspend-contact',
    },
    {
      icon: <IconSvg height="30px" svgComponent={<ReactivateIcon />} width="30px" />,
      handleOnClick: handleReactivateDiscardedUsers,
      tooltip: t('REACTIVATE DISCARDED USER.tooltip'),
      flagOn: discardedView,
      disabled: !isValidReactivateDiscardedUsers,
      disabledTooltip: t('REACTIVATE DISCARDED USER.tooltipDisabled'),
      id: 'action-reactivate-contact',
    },
    {
      icon: <DeleteIconSolid />,
      handleOnClick: () =>
        openModal({
          element: (
            <DeleteModal
              description={t(
                contactSelection === 1
                  ? 'CONTACT_DELETE_MODAL.description'
                  : 'CONTACT_DELETE_MODAL.descriptionPlural'
              )}
              setConfirmDelete={setConfirmDelete}
              title={t(
                contactSelection === 1
                  ? 'CONTACT_DELETE_MODAL.title'
                  : 'CONTACT_DELETE_MODAL.titlePlural'
              )}
            />
          ),
        }),
      tooltip: t(
        contactSelection === 1 ? 'CONTACT_DELETE_MODAL.title' : 'CONTACT_DELETE_MODAL.titlePlural'
      ),
      flagOn: true,
      disabled: false,
      disabledTooltip: '',
      id: 'action-delete',
    },
  ];

  return (
    <ActionBar>
      <div>
        <section className="flex h-full items-center">
          <div className="text-xs ml-10 text-white" style={{ fontSize: '12px' }}>
            {`${quantifier(contactSelection)} ${t('CONTACTS_ACTIONS_DROPDOWN.Selected')}`}
          </div>
          <div className="ml-4">
            {actionBarDropdown && (
              <ContactsDropDown
                icon={<DropdownCellPhoneIcon />}
                title="Actions"
                {...dropdownOptions}
              />
            )}
          </div>
          <div className="ml-5 flex">
            {iconsActions.map((element, i) => {
              return (
                element.flagOn && (
                  <div key={i} className="mr-2">
                    <Button
                      outline
                      primary
                      standard
                      disabled={element.disabled}
                      iconLeft={element.icon}
                      id={element.id}
                      tooltip={element.disabled ? element.disabledTooltip : element.tooltip}
                      onClick={element.handleOnClick}
                    />
                  </div>
                )
              );
            })}
          </div>
        </section>
      </div>
      <Modal open={modalIsOpen} onClose={setModalIsOpen}>
        {childrenModal}
      </Modal>
    </ActionBar>
  );
};
